import React, { useEffect, useState, useMemo, memo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiPlus, FiSave, FiEdit3, FiTrash2 } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeaderSetting from "../shared/pageHeader/PageHeaderSetting";
import PerfectScrollbar from "react-perfect-scrollbar";
import Footer from "../shared/Footer";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { Link } from "react-router-dom"; 


// =========================================================
// 1) Status Switch için ayrı bir component (memo'lu)
// =========================================================
const StatusSwitch = memo(({ ipId, currentStatus, fetchIPList }) => {
  const [isChecked, setIsChecked] = useState(currentStatus === "active");

  // Status'i toggle eden fonksiyon
  const toggleStatus = async () => {
    const newStatus = isChecked ? "inactive" : "active";
    setIsChecked(!isChecked); // Önce anında değiştir
    try {
      // Burada backend endpoint'inizi kendi yapınıza göre düzenleyin
      // Örn: PUT /api/ip/update-status/:id
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/ip/update-status/${ipId}`, {
        status: newStatus,
      });
      // Güncel tablo verisini tekrar çek
      fetchIPList();
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      // Hata alırsa geri döndür
      setIsChecked(isChecked);
      toast.error("Durum güncellenemedi!");
    }
  };

  return (
    <div className="form-check form-switch text-end d-flex align-items-end justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{
          cursor: "pointer",
          width: "45px",  // Switch boyutu
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{
          padding: "6px 12px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "bold",
          transition: "0.3s ease-in-out",
        }}
      >

      </span>
    </div>
  );
});


// =========================================================
// 2) Asıl tablo + yeni ekle formu
// =========================================================
const SettingsIPForm = () => {

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const [ipList, setIpList] = useState([]);
  const [newRows, setNewRows] = useState([
    { id: null, title: "", ip: "", created_at: new Date().toISOString() },
  ]);
  const [editingIp, setEditingIp] = useState(null);

  // Backend URL
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ---------------------------
  // IP Listesini Çek
  // ---------------------------
  const fetchIPList = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/ip`);
      setIpList(response.data);
    } catch (error) {
      console.error("❌ IP verileri alınamadı:", error);
      toast.error("IP verileri alınamadı!");
    }
  };

  useEffect(() => {
    fetchIPList();
  }, []);

  // ---------------------------
  // Status'ü GET ile çekmek yerine
  //   ipList içinde "status" alanı olduğunu varsayıyoruz.
  //   (Eğer yoksa her IP için ek bir GET isteği atmanız gerekir.)
  // ---------------------------

  // ---------------------------
  // Tablo Kolonları (title, ip, tarih, durum, işlemler)
  // ---------------------------
  const columns = useMemo(() => [
    {
      accessorKey: "title",
      header: getText("TABLE_TITLE_LABEL"),
      meta: { headerClassName: "text-start key-column", className: "text-start key-column" },
      size: 200,
    },
    {
      accessorKey: "ip",
      header: getText("TABLE_IP_ADDRESS_LABEL"),
      meta: { headerClassName: "text-start key-column ", className: "text-start key-column" },
      size: 200,
    },
    {
      accessorKey: "created_at",
      header: () => (
        <div style={{ textAlign: 'end', width: '120px' }}>
          {getText("TABLE_DATE_LABEL")}
        </div>
      ),
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 150,
      cell: (info) => {
        const rawDate = info.getValue();
        // Tarihi dilediğiniz biçimde gösterebilirsiniz
        const formattedDate = new Date(rawDate).toLocaleDateString('en-GB');
        return <div style={{ textAlign: 'end' }}>{formattedDate}</div>;
      },
    },
    {
      accessorKey: "status",
      header: () => (
        <div style={{ textAlign: "end", width: "130px" }}>{getText("TABLE_STATUS_LABEL")}</div>
      ),
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 130,
      cell: (info) => {
        const rowData = info.row.original;
        return (
          <div style={{ textAlign: "end" }}>
            {/* StatusSwitch component */}
            <StatusSwitch
              ipId={rowData.id}
              currentStatus={rowData.status || "inactive"}
              fetchIPList={fetchIPList}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "actions",
      header: () => (
        <div style={{ textAlign: "right", paddingRight: "30px" }}>{getText("TABLE_ACTIONS_LABEL")}</div>
      ),
      enableSorting: false,
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 180,
      cell: (info) => {
        const rowData = info.row.original;
        const ipId = rowData.id;
        return (
          <div
            className="hstack gap-2 justify-content-end"
            style={{ paddingRight: "10px" }}
          >
            <Link
            to={`/settings/ip/edit/${ipId}`}
            className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
            title={getText("EDIT")}
          >
            <FiEdit3 />
          </Link>

        
            {/* Sil */}
            <button
              className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
              onClick={() => deleteIP(ipId)}
              title={getText("DELETE")}
            >
              <FiTrash2 />
            </button>
          </div>
        );
      },
    },
  ], [translations],);

  // ---------------------------
  // Yeni IP veya Güncelleme Kaydet
  // ---------------------------
  const saveIPs = async () => {
    // Boş satırları temizle
    const validRows = newRows.filter((row) => row.title.trim() && row.ip.trim());
    if (!validRows.length) {
      toast.warn("⚠️ En az 1 geçerli IP girmelisiniz!");
      return;
    }

    try {
      for (const row of validRows) {
        if (row.id) {
          // Güncelleme
          await axios.put(`${BASE_URL}/api/ip/${row.id}`, {
            title: row.title,
            ip: row.ip,
            created_at: row.created_at, // DB tarafında isterseniz güncellenebilir
          });
        } else {
          // Yeni ekleme
          await axios.post(`${BASE_URL}/api/ip`, {
            title: row.title,
            ip: row.ip,
            status: "active",
            created_at: new Date().toISOString(),
          });
        }
      }
      toast.success(getText("SUCCESS_IP_SAVED"));
      // Inputları temizle
      setNewRows([
        { id: null, title: "", ip: "", created_at: new Date().toISOString() },
      ]);
      fetchIPList();
      setEditingIp(null);
    } catch (error) {
      console.error("❌ IP kaydedilemedi:", error);
      toast.error("IP kaydedilemedi!");
    }
  };

  // ---------------------------
  // IP Düzenleme
  // ---------------------------
  const editIP = (id) => {
    const ipData = ipList.find((item) => item.id === id);
    if (ipData) {
      setEditingIp(ipData.id);
      setNewRows([
        {
          id: ipData.id,
          title: ipData.title,
          ip: ipData.ip,
          created_at: ipData.created_at,
        },
      ]);
    }
  };

  // ---------------------------
  // IP Silme
  // ---------------------------
  const deleteIP = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/ip/${id}`);
      toast.error("🗑️ IP başarıyla silindi!");
      fetchIPList();
    } catch (error) {
      console.error("❌ IP silinemedi:", error);
      toast.error("IP silinemedi!");
    }
  };

  // ---------------------------
  // Yeni Satır Ekle
  // ---------------------------
  const addNewRow = () => {
    setNewRows([
      ...newRows,
      { id: null, title: "", ip: "", created_at: new Date().toISOString() },
    ]);
  };

  // ---------------------------
  // Input Değişiklikleri
  // ---------------------------
  const handleInputChange = (index, field, value) => {
    const updatedRows = [...newRows];
    updatedRows[index][field] = value;
    setNewRows(updatedRows);
  };

  // ----------------------------------------------------------
  // SAYFA JSX
  // ----------------------------------------------------------
  return (
    <div className="content-area setting-form">
      <ToastContainer />
      <PerfectScrollbar>
        <PageHeaderSetting />
        <div className="content-area-body">
        
            <div className="card-body">
      
      {/* =========================
          1) IP Listesi KARTI
      ========================= */}
    
 

        {/* IP Tablosu */}
        <Table data={ipList} columns={columns} />
     

      {/* =========================
          2) Yeni IP Ekleme/Güncelleme KARTI
      ========================= */}
      <div className="card card-body general-info">
        <div className="mb-4 d-flex align-items-center justify-content-between">
          <h5 className="fw-bold text-primary mb-0">
          {editingIp ? getText("LABEL_UPDATE_IP") : getText("LABEL_NEW_IP")}
          </h5>
        </div>

        {newRows.map((row, index) => (
          <div key={index} className="border rounded p-3 mb-3">
            <div className="row">
              {/* Başlık */}
              <div className="col-lg-4">
                <label className="form-label fw-bold">{getText("MENU_SETTINGS_TITLE")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={getText("MENU_SETTINGS_TITLE")}
                  value={row.title}
                  onChange={(e) =>
                    handleInputChange(index, "title", e.target.value)
                  }
                />
              </div>

              {/* IP Adresi */}
              <div className="col-lg-4">
                <label className="form-label fw-bold">{getText("LABEL_IP_ADDRESS")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="ex: 192.168.1.1"
                  value={row.ip}
                  onChange={(e) =>
                    handleInputChange(index, "ip", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}

        {/* Yeni Satır Ekle Butonu */}
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-outline-primary" onClick={addNewRow}>
            <FiPlus /> {getText("ADD_NEW_ROW")}
          </button>
        </div>

        {/* Kaydet Butonu */}
        <div className="d-flex justify-content-center">
          <button className="btn btn-primary" onClick={saveIPs}>
            <FiSave className="me-2" /> {getText("SAVE_BTN")}
          </button>
        </div>
      </div>
      </div>
      </div>
    
       
              </PerfectScrollbar>
    </div>
  );
};

export default SettingsIPForm;
